/**
 * Hook: useTenantBillingAdmin
 * Descrição: Versão admin do useTenantBilling que recebe tenantId como parâmetro
 * 
 * Usado pelo Root Admin para visualizar dados de billing de qualquer tenant
 * Sem ações de checkout/portal (apenas visualização)
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação para Admin Tenant Management
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

// Reutilizar interfaces do hook original
interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string | null;
  price_monthly: number;
  price_yearly?: number | null;
  features: string[] | null;
  max_inboxes: number;
  max_agents: number;
  is_active: boolean;
}

interface TenantBilling {
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  billing_period: string;
  current_period_end: string | null;
  current_period_start: string | null;
  trial_ends_at: string | null;
  plan: string | null;
}

interface BillingHistoryItem {
  id: string;
  created_at: string;
  amount_paid: number;
  currency: string;
  status: string;
  plan_name: string | null;
  billing_period: string | null;
  period_start: string | null;
  period_end: string | null;
  invoice_url: string | null;
  invoice_pdf: string | null;
  stripe_invoice_id: string | null;
  line_items: unknown;
}

export function useTenantBillingAdmin(tenantId: string | null) {
  // Buscar planos disponíveis
  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("id, name, description, price_monthly, price_yearly, features, max_inboxes, max_agents, is_active")
        .eq("is_active", true)
        .order("price_monthly", { ascending: true });

      if (error) throw error;
      
      return (data || []) as unknown as SubscriptionPlan[];
    },
    staleTime: 10 * 60 * 1000,
  });

  // Buscar billing do tenant específico
  const { data: billing, isLoading: billingLoading } = useQuery({
    queryKey: ["admin-tenant-billing", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;

      logger.debug("useTenantBillingAdmin", "Buscando billing do tenant", { tenantId });

      const { data, error } = await supabase
        .from("tenants")
        .select(`
          stripe_customer_id,
          stripe_subscription_id,
          subscription_status,
          billing_period,
          current_period_end,
          current_period_start,
          trial_ends_at,
          plan
        `)
        .eq("id", tenantId)
        .single();

      if (error) {
        logger.error("useTenantBillingAdmin", "Erro ao buscar billing", { error });
        throw error;
      }

      return data as TenantBilling;
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Buscar histórico de faturas
  const { data: billingHistory, isLoading: historyLoading } = useQuery({
    queryKey: ["admin-tenant-billing-history", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug("useTenantBillingAdmin", "Buscando histórico de billing", { tenantId });

      const { data, error } = await supabase
        .from("billing_history")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false })
        .limit(12);

      if (error) {
        logger.error("useTenantBillingAdmin", "Erro ao buscar histórico", { error });
        throw error;
      }

      return (data || []) as BillingHistoryItem[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Plano atual
  const currentPlan = billing?.plan || "free";

  // Verificar trial
  const isInTrial = !!(billing?.trial_ends_at && new Date(billing.trial_ends_at) > new Date());
  const trialDaysRemaining = isInTrial && billing?.trial_ends_at
    ? Math.ceil((new Date(billing.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;

  // Verificar assinatura ativa
  const hasActiveSubscription = billing?.subscription_status === "active" || billing?.subscription_status === "trialing";

  // Sync subscription (admin pode acionar sync também)
  const syncSubscription = async () => {
    if (!tenantId) return;
    
    logger.info("useTenantBillingAdmin", "Admin sincronizando subscription", { tenantId });
    
    // Chamar edge function de check-subscription
    const { error } = await supabase.functions.invoke("check-subscription", {
      body: { tenant_id: tenantId },
    });

    if (error) {
      logger.error("useTenantBillingAdmin", "Erro ao sincronizar", { error });
      throw error;
    }
  };

  return {
    plans,
    billing,
    billingHistory,
    currentPlan,
    isInTrial,
    trialDaysRemaining,
    hasActiveSubscription,
    isLoading: plansLoading || billingLoading,
    historyLoading,
    syncSubscription,
    isSyncingSubscription: false, // Admin não tem estado de loading para sync
  };
}
